/**
 * Integrations dashboard — partner webhook inspector (RM / NI / Admin only).
 *
 * Initially scoped to the Hire Connect ↔ Care Connect bridge. Future
 * partners would extend this page with additional cards.
 */

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plug, RefreshCw, Inbox, AlertTriangle, KeyRound, ExternalLink, Mail, RotateCcw, CheckCircle2 } from "lucide-react";
import { fmtDateTimeShort } from "@/lib/dates";

const STATUS_COLOURS = {
  processed: "bg-emerald-100 text-emerald-800",
  received: "bg-slate-100 text-slate-700",
  ignored: "bg-slate-100 text-slate-500",
  error: "bg-red-100 text-red-800",
};

const EVENT_LABELS = {
  "webhook.test": "Test ping",
  "training.created": "Training created",
  "training.updated": "Training updated",
  "training.expiring": "Training expiring",
  "training.archived": "Training archived",
  "dbs.updated": "DBS updated",
  "leave.created": "Leave requested",
  "leave.approved": "Leave approved",
  "leave.declined": "Leave declined",
  "leave.cancelled": "Leave cancelled",
};

export default function Integrations() {
  const [cfg, setCfg] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [busy, setBusy] = useState(false);
  const [replayBusy, setReplayBusy] = useState({});

  const load = async () => {
    setBusy(true);
    try {
      const [c, d, u] = await Promise.all([
        api.get("/integrations/recruitment/config"),
        api.get("/integrations/recruitment/deliveries", { params: { limit: 50 } }),
        api.get("/integrations/recruitment/unmatched"),
      ]);
      setCfg(c.data);
      setDeliveries(d.data.items || []);
      setUnmatched(u.data.items || []);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, []);

  const replay = async (email) => {
    setReplayBusy((s) => ({ ...s, [email]: true }));
    try {
      const { data } = await api.post("/integrations/recruitment/unmatched/replay", { work_email: email });
      alert(`Replayed ${data.replayed} of ${data.buffered_total} buffered payload(s).`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Replay failed.");
    } finally {
      setReplayBusy((s) => ({ ...s, [email]: false }));
    }
  };

  const endpointUrl = cfg
    ? `${window.location.origin.replace("https://care-manage-1", "https://care-manage-1")}${cfg.endpoint_path}`
    : "";

  const counts = deliveries.reduce(
    (acc, d) => {
      const s = d.status || "received";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    {},
  );

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 inline-flex items-center gap-2">
            <Plug className="h-6 w-6 text-[#0D7377]" /> Integrations
          </h1>
          <p className="text-sm text-slate-600 mt-1">Partner systems that push data into Care Connect.</p>
        </div>
        <button
          onClick={load}
          disabled={busy}
          data-testid="integrations-refresh"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Hire Connect card */}
      <section className="bg-white rounded-lg border border-slate-200 shadow-sm" data-testid="integration-hire-connect">
        <header className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-b border-slate-100">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Hire Connect — Recruitment & HR</h2>
            <p className="text-xs text-slate-500">
              Inbound webhooks for Training, DBS and Leave. Matched to staff by work email.
            </p>
          </div>
          <span
            className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${cfg?.configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
            data-testid="integration-hire-connect-status"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${cfg?.configured ? "bg-emerald-500" : "bg-amber-500"}`} />
            {cfg?.configured ? "Live" : "Not configured"}
          </span>
        </header>

        {/* Config details */}
        <div className="px-5 py-4 grid md:grid-cols-2 gap-4 border-b border-slate-100">
          <div>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 inline-flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Endpoint URL
            </p>
            <code className="block mt-1 text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5 break-all">
              {endpointUrl || "—"}
            </code>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 inline-flex items-center gap-1">
              <KeyRound className="h-3 w-3" /> X-API-Key (masked)
            </p>
            <code className="block mt-1 text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1.5">
              {cfg?.api_key_masked || "(unset)"}
            </code>
            <p className="text-[10px] text-slate-500 mt-1">
              The full value lives in <code>RECRUITMENT_WEBHOOK_KEY</code> on the backend. Paste it into Hire Connect → Integrations → Care Connect.
            </p>
          </div>
        </div>

        {/* Counters */}
        <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-slate-100">
          <Counter label="Processed (last 50)" value={counts.processed || 0} tone="green" />
          <Counter label="Errors (last 50)" value={counts.error || 0} tone={(counts.error || 0) > 0 ? "red" : "slate"} />
          <Counter label="Ignored" value={counts.ignored || 0} tone="slate" />
          <Counter label="Unmatched buffer" value={unmatched.length} tone={unmatched.length ? "amber" : "slate"} />
        </div>

        {/* Recent deliveries table */}
        <div className="px-5 py-3 border-b border-slate-100">
          <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5 mb-2"><Inbox className="h-4 w-4 text-slate-500" /> Recent deliveries</p>
          {deliveries.length === 0 ? (
            <p className="text-xs text-slate-500 px-2 py-3" data-testid="integrations-deliveries-empty">No deliveries yet. Have Hire Connect send a <code>webhook.test</code> ping to confirm the link.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-100">
                    <th className="py-1.5 pr-3">Received</th>
                    <th className="py-1.5 pr-3">Event</th>
                    <th className="py-1.5 pr-3">Status</th>
                    <th className="py-1.5 pr-3">Matched</th>
                    <th className="py-1.5 pr-3">Delivery id</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.map((d) => {
                    const matched = (d.result || {}).matched;
                    return (
                      <tr key={d.delivery_id || d.received_at} className="border-b border-slate-50" data-testid={`integrations-delivery-${d.delivery_id || d.event}`}>
                        <td className="py-1.5 pr-3 whitespace-nowrap text-slate-700">{fmtDateTimeShort(d.received_at)}</td>
                        <td className="py-1.5 pr-3 font-medium text-slate-900">{EVENT_LABELS[d.event] || d.event}</td>
                        <td className="py-1.5 pr-3">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${STATUS_COLOURS[d.status] || STATUS_COLOURS.received}`}>
                            {d.status}
                          </span>
                        </td>
                        <td className="py-1.5 pr-3">
                          {matched === true && <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> yes</span>}
                          {matched === false && <span className="text-amber-700">unmatched</span>}
                          {matched === undefined && <span className="text-slate-400">—</span>}
                        </td>
                        <td className="py-1.5 pr-3 font-mono text-[10px] text-slate-500 break-all">{d.delivery_id || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Unmatched buffer */}
        <div className="px-5 py-3">
          <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5 mb-2"><AlertTriangle className="h-4 w-4 text-amber-600" /> Unmatched payloads</p>
          <p className="text-[11px] text-slate-500 mb-2">
            Payloads that arrived for an email we couldn't find. Create the staff account in Care Connect, then click <em>Replay</em> to apply the buffered data.
          </p>
          {unmatched.length === 0 ? (
            <p className="text-xs text-slate-500 px-2 py-3" data-testid="integrations-unmatched-empty">No buffered payloads — every delivery so far has matched a staff member.</p>
          ) : (
            <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
              {unmatched.map((u, i) => (
                <li key={i} data-testid={`integrations-unmatched-${i}`} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm text-slate-900 inline-flex items-center gap-1.5">
                      <Mail className="h-3.5 w-3.5 text-slate-400" />
                      <span className="font-mono text-xs">{u.work_email}</span>
                      <span className="text-slate-400">·</span>
                      <span>{EVENT_LABELS[u.event] || u.event}</span>
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {fmtDateTimeShort(u.received_at)} · {u.full_name || "(name unknown)"} · kind: {u.kind}
                    </p>
                  </div>
                  <button
                    onClick={() => replay(u.work_email)}
                    disabled={!!replayBusy[u.work_email]}
                    data-testid={`integrations-unmatched-replay-${i}`}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <RotateCcw className={`h-3 w-3 ${replayBusy[u.work_email] ? "animate-spin" : ""}`} /> Replay
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function Counter({ label, value, tone }) {
  const tones = {
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    red: "bg-red-50 border-red-200 text-red-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`rounded-md border px-3 py-2 ${tones[tone] || tones.slate}`} data-testid={`integrations-counter-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</p>
      <p className="text-2xl font-semibold leading-none mt-1">{value}</p>
    </div>
  );
}
